// Datenbasis pro Gerätetyp (PRD §14). Regelbasiert, deterministisch.
// Jeder Eintrag liefert: Berechnungsmodus, Verbrauchsspannen (low/typical/high),
// Standard-Nutzungsprofil, die Felder für beide Eingabewege sowie den
// einzigartigen erklärenden Content (intro, drivers, example, FAQ) für die
// SEO-Landingpage. Nichts hiervon wird generiert — alles ist redaktionell.

export const devices = {
  heizluefter: {
    slug: "heizluefter-stromkosten",
    key: "heizluefter",
    name: "Heizlüfter",
    icon: "Flame",
    mode: "watt",
    h1: "Heizlüfter Stromkosten berechnen",
    metaTitle: "Heizlüfter Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Heizlüfter pro Tag, Monat und Winter an Strom? Mit Wattzahl rechnen oder schätzen lassen. Kostenloser Rechner mit Spanne.",
    intro:
      "Heizlüfter ziehen viel Strom, weil sie Wärme direkt aus elektrischer Leistung erzeugen. Schon wenige Stunden täglich summieren sich spürbar. Hier rechnest du es genau aus, auch wenn du die Wattzahl nicht kennst.",
    // Schätzspanne (PRD §8.4)
    ranges: { low: 1000, typical: 2000, high: 2500 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 3, daysPerWeek: 7 },
    price: 0.35,
    // Exakte Eingabe
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 2000, min: 100, max: 3500, step: 100 },
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 3, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    // Geführte Schätzung: Auswahl bestimmt die Watt-Stufe
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 3, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Wir rechnen mit drei üblichen Leistungsstufen. Niedrig entspricht einem kleinen Gerät, hoch einem starken Heizlüfter auf voller Stufe.",
    },
    explanation:
      "Die Formel ist einfach: Leistung in Kilowatt mal Stunden ergibt den Verbrauch in Kilowattstunden, multipliziert mit dem Strompreis ergibt die Kosten. Ein Heizlüfter mit 2.000 Watt zieht in einer Stunde 2 Kilowattstunden.",
    drivers: [
      "Leistungsstufe: zwischen 1.000 und 2.500 Watt ist alles üblich.",
      "Laufzeit pro Tag: jede zusätzliche Stunde kostet direkt mehr.",
      "Strompreis: bei höheren Tarifen steigt alles proportional.",
      "Thermostat: ein Gerät, das öfter abschaltet, verbraucht im Schnitt weniger.",
    ],
    example: {
      label: "Beispiel: 2.000 Watt, 3 Stunden täglich, 0,35 €/kWh",
      lines: [
        "Verbrauch pro Tag: 6 kWh",
        "Kosten pro Tag: 2,10 €",
        "Kosten pro Woche: 14,70 €",
        "Kosten pro Monat: rund 63,80 €",
      ],
    },
    faq: [
      { q: "Wie viel Strom verbraucht ein Heizlüfter pro Stunde?", a: "Ein typischer Heizlüfter mit 2.000 Watt verbraucht in einer Stunde 2 Kilowattstunden. Bei 0,35 € pro Kilowattstunde sind das 0,70 € pro Stunde." },
      { q: "Lohnt sich ein Heizlüfter zum Heizen?", a: "Für kurzes, punktuelles Zuheizen kann er sinnvoll sein. Als Dauerheizung ist er teuer, weil er Strom direkt in Wärme umwandelt und Strom je Kilowattstunde meist deutlich teurer ist als Gas oder Fernwärme." },
      { q: "Warum ist die Rechnung nur eine Schätzung?", a: "Heizlüfter laufen selten konstant auf voller Stufe. Ein Thermostat schaltet zwischendurch ab. Deshalb zeigen wir eine Spanne von niedrig bis hoch statt eines einzelnen Werts." },
      { q: "Wie senke ich die Stromkosten eines Heizlüfters?", a: "Kürzere Laufzeiten, niedrigere Stufe, ein Thermostat und gezieltes Heizen einzelner Räume helfen. Wer dauerhaft Wärme braucht, fährt mit der vorhandenen Zentralheizung meist günstiger." },
    ],
  },

  trockner: {
    slug: "trockner-stromkosten",
    key: "trockner",
    name: "Trockner",
    icon: "Wind",
    mode: "kwh_per_cycle",
    h1: "Trockner Stromkosten berechnen",
    metaTitle: "Trockner Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Wäschetrockner pro Durchgang, Monat und Jahr? Wärmepumpe, Kondens- oder Ablufttrockner. Kostenloser Rechner mit Spanne.",
    intro:
      "Beim Trockner zählt vor allem der Verbrauch pro Durchgang, und der hängt stark vom Gerätetyp ab. Wärmepumpentrockner sind sparsam, Kondens- und Ablufttrockner brauchen deutlich mehr.",
    // kWh pro Durchgang (PRD §14 Beispiel)
    ranges: { low: 1.5, typical: 2.5, high: 4.0 },
    rangeUnit: "kWh/Durchgang",
    defaultUsage: { cyclesPerWeek: 3 },
    price: 0.35,
    exact: {
      fields: [
        { id: "kwhPerCycle", label: "kWh pro Durchgang", unit: "kWh", default: 2.5, min: 0.5, max: 6, step: 0.1 },
        { id: "cyclesPerWeek", label: "Durchgänge pro Woche", unit: "x", default: 3, min: 1, max: 21, step: 1 },
      ],
    },
    estimate: {
      // Gerätetyp wählt die kWh-Spanne (PRD §8.4)
      typeField: {
        id: "trocknertyp",
        label: "Welcher Trocknertyp?",
        options: [
          { value: "waermepumpe", label: "Wärmepumpentrockner", ranges: { low: 1.0, typical: 1.5, high: 2.2 } },
          { value: "kondens", label: "Kondenstrockner", ranges: { low: 2.8, typical: 3.5, high: 4.5 } },
          { value: "abluft", label: "Ablufttrockner", ranges: { low: 3.0, typical: 3.8, high: 4.8 } },
          { value: "unbekannt", label: "Weiß ich nicht", ranges: { low: 1.5, typical: 2.5, high: 4.0 } },
        ],
      },
      profileFields: [
        { id: "cyclesPerWeek", label: "Durchgänge pro Woche", unit: "x", default: 3, min: 1, max: 21, step: 1 },
      ],
      assumptionText:
        "Der Gerätetyp bestimmt die Verbrauchsspanne. Wähle Wärmepumpe, wenn dein Trockner sparsam läuft, sonst Kondens oder Abluft.",
    },
    explanation:
      "Ein Trockner wird über den Verbrauch pro Durchgang gerechnet: kWh pro Durchgang mal Strompreis ergibt die Kosten pro Trocknung. Mal Anzahl der Durchgänge ergibt Woche, Monat und Jahr.",
    drivers: [
      "Gerätetyp: Wärmepumpentrockner brauchen oft nur etwa ein Drittel eines Ablufttrockners.",
      "Beladung und Restfeuchte: nasse oder zu kleine Ladungen erhöhen den Verbrauch pro Kilo.",
      "Programm: Schranktrocken kostet mehr als bügelfeucht.",
      "Anzahl der Durchgänge pro Woche.",
    ],
    example: {
      label: "Beispiel: Kondenstrockner mit 3,5 kWh, 3 Durchgänge pro Woche, 0,35 €/kWh",
      lines: [
        "Kosten pro Durchgang: 1,23 €",
        "Kosten pro Woche: 3,68 €",
        "Kosten pro Monat: rund 16,00 €",
        "Kosten pro Jahr: rund 191 €",
      ],
    },
    faq: [
      { q: "Was kostet ein Trocknerdurchgang?", a: "Je nach Gerät zwischen rund 0,50 € und 1,60 €. Ein Wärmepumpentrockner liegt bei etwa 1,5 kWh pro Durchgang, ein Kondens- oder Ablufttrockner deutlich höher." },
      { q: "Wie viel spart ein Wärmepumpentrockner?", a: "Ein Wärmepumpentrockner braucht oft nur etwa die Hälfte bis ein Drittel eines Kondens- oder Ablufttrockners. Bei häufigem Trocknen amortisiert sich der höhere Anschaffungspreis mit der Zeit." },
      { q: "Warum schwankt der Verbrauch so stark?", a: "Restfeuchte der Wäsche, Beladung, gewähltes Programm und der Gerätetyp wirken zusammen. Deshalb zeigen wir eine Spanne statt eines einzelnen Werts." },
      { q: "Wie senke ich die Trocknerkosten?", a: "Wäsche gut schleudern, den Trockner voll beladen, ein sparsames Programm wählen und bei einem Neukauf einen Wärmepumpentrockner bevorzugen." },
    ],
  },

  "gaming-pc": {
    slug: "gaming-pc-stromkosten",
    key: "gaming-pc",
    name: "Gaming-PC",
    icon: "Gamepad2",
    mode: "watt",
    h1: "Gaming-PC Stromkosten berechnen",
    metaTitle: "Gaming-PC Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Gaming-PC pro Tag, Monat und Jahr an Strom? Einsteiger, Mittelklasse oder High-End. Kostenloser Rechner mit realistischer Spanne.",
    intro:
      "Ein Gaming-PC verbraucht beim Spielen ein Vielfaches dessen, was er im Idle oder bei Office zieht. Wie hoch die Kosten sind, hängt vor allem von der Hardwareklasse und der Spielzeit ab.",
    ranges: { low: 180, typical: 320, high: 500 },
    rangeUnit: "W (Durchschnitt)",
    defaultUsage: { hoursPerDay: 3, daysPerWeek: 7 },
    price: 0.35,
    exact: {
      fields: [
        { id: "watt", label: "Durchschnittliche Leistung", unit: "W", default: 320, min: 50, max: 900, step: 10 },
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 3, min: 0.5, max: 16, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      typeField: {
        id: "pcklasse",
        label: "Welche PC-Klasse?",
        options: [
          { value: "einsteiger", label: "Einsteiger", ranges: { low: 90, typical: 150, high: 250 } },
          { value: "mittelklasse", label: "Mittelklasse", ranges: { low: 180, typical: 320, high: 450 } },
          { value: "highend", label: "High-End", ranges: { low: 300, typical: 500, high: 750 } },
        ],
      },
      profileFields: [
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 3, min: 0.5, max: 16, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Wir rechnen mit einer durchschnittlichen Leistung über die Nutzungszeit. Reines Gaming liegt am oberen Rand, Office und Idle am unteren.",
    },
    explanation:
      "Gerechnet wird über die durchschnittliche Leistung: Watt mal Stunden ergibt Kilowattstunden, mal Strompreis die Kosten. Der Knackpunkt ist die Durchschnittsleistung, denn Gaming und Idle liegen weit auseinander.",
    drivers: [
      "Hardwareklasse: vor allem Grafikkarte und Prozessor bestimmen die Spitzenlast.",
      "Anteil Gaming gegenüber Office und Idle.",
      "Monitor und Peripherie kommen je nach Setup dazu.",
      "Stunden pro Tag und Tage pro Woche.",
    ],
    example: {
      label: "Beispiel: Mittelklasse mit 320 W Durchschnitt, 3 Stunden täglich, 0,35 €/kWh",
      lines: [
        "Verbrauch pro Tag: rund 0,96 kWh",
        "Kosten pro Tag: rund 0,34 €",
        "Kosten pro Monat: rund 10,20 €",
        "Kosten pro Jahr: rund 122 €",
      ],
    },
    faq: [
      { q: "Wie viel Strom verbraucht ein Gaming-PC?", a: "Beim Spielen meist zwischen 200 und 600 Watt, im Idle oder bei Office deutlich weniger. Über den Tag gemittelt liegt ein Mittelklasse-System oft bei rund 300 Watt." },
      { q: "Was kostet Zocken pro Stunde?", a: "Bei 320 Watt Durchschnitt und 0,35 € pro Kilowattstunde kostet eine Stunde rund 0,11 €. Ein High-End-System mit 500 Watt liegt bei etwa 0,18 € pro Stunde." },
      { q: "Zählt der Monitor mit?", a: "Ja, ein Monitor zieht je nach Größe zusätzlich etwa 20 bis 60 Watt. Wenn du den Monitor separat betreibst, rechne ihn zur Durchschnittsleistung hinzu." },
      { q: "Wie senke ich den Stromverbrauch beim Gaming?", a: "Ein FPS-Limit, Energiesparmodi außerhalb des Spiels und das echte Ausschalten statt Standby helfen. Auch eine effiziente Grafikkarte macht über das Jahr einen Unterschied." },
    ],
  },

  kuehlschrank: {
    slug: "kuehlschrank-stromkosten",
    key: "kuehlschrank",
    name: "Kühlschrank",
    icon: "Refrigerator",
    mode: "kwh_per_year",
    h1: "Kühlschrank Stromkosten berechnen",
    metaTitle: "Kühlschrank Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Kühlschrank pro Jahr, Monat und Tag an Strom? Über den Jahresverbrauch berechnen oder schätzen lassen. Kostenloser Rechner mit Spanne.",
    intro:
      "Ein Kühlschrank läuft das ganze Jahr durch, deshalb rechnet man ihn am besten über den Jahresverbrauch in Kilowattstunden. Alte Geräte verbrauchen oft ein Mehrfaches moderner Modelle.",
    // kWh pro Jahr
    ranges: { low: 100, typical: 160, high: 300 },
    rangeUnit: "kWh/Jahr",
    defaultUsage: {},
    price: 0.35,
    exact: {
      fields: [
        { id: "kwhPerYear", label: "Verbrauch pro Jahr", unit: "kWh", default: 160, min: 50, max: 600, step: 10 },
      ],
    },
    estimate: {
      typeField: {
        id: "altersklasse",
        label: "Wie alt ist der Kühlschrank?",
        options: [
          { value: "neu", label: "Neu, effizient (ab 2021)", ranges: { low: 75, typical: 110, high: 160 } },
          { value: "mittel", label: "Einige Jahre alt", ranges: { low: 130, typical: 180, high: 250 } },
          { value: "alt", label: "Alt (über 10 Jahre)", ranges: { low: 200, typical: 300, high: 450 } },
        ],
      },
      profileFields: [],
      assumptionText:
        "Das Alter ist der wichtigste Faktor. Ein altes Gerät ohne moderne Effizienz verbraucht oft das Doppelte eines neuen.",
    },
    explanation:
      "Beim Kühlschrank gilt: Jahresverbrauch in Kilowattstunden mal Strompreis ergibt die Jahreskosten. Geteilt durch zwölf ergibt den Monat, durch 365 den Tag. Der Jahresverbrauch steht oft auf dem Energielabel.",
    drivers: [
      "Alter und Effizienzklasse: der mit Abstand größte Hebel.",
      "Größe und Bauart: größere Geräte und Gefrierkombis brauchen mehr.",
      "Aufstellort: neben Herd oder Heizung steigt der Verbrauch.",
      "Innentemperatur: jedes Grad kälter kostet zusätzlich Strom.",
    ],
    example: {
      label: "Beispiel: 160 kWh pro Jahr, 0,35 €/kWh",
      lines: [
        "Kosten pro Jahr: 56,00 €",
        "Kosten pro Monat: rund 4,67 €",
        "Kosten pro Tag: rund 0,15 €",
      ],
    },
    faq: [
      { q: "Wie viel Strom verbraucht ein Kühlschrank im Jahr?", a: "Moderne Geräte liegen oft bei 75 bis 160 Kilowattstunden pro Jahr, ältere bei 250 bis 450. Den genauen Wert findest du meist auf dem Energielabel." },
      { q: "Was kostet ein alter Kühlschrank pro Jahr?", a: "Ein altes Gerät mit 300 Kilowattstunden kostet bei 0,35 € pro Kilowattstunde rund 105 € im Jahr. Ein effizientes neues Modell kann das halbieren." },
      { q: "Lohnt sich der Austausch eines alten Kühlschranks?", a: "Wenn ein über zehn Jahre altes Gerät viel verbraucht, kann sich ein effizienter Neukauf über mehrere Jahre rechnen. Vergleiche den geschätzten Jahresverbrauch alt gegen neu." },
      { q: "Wie senke ich den Stromverbrauch des Kühlschranks?", a: "Nicht zu kalt einstellen, regelmäßig abtauen, die Dichtung prüfen und das Gerät nicht direkt neben Wärmequellen aufstellen." },
    ],
  },

  router: {
    slug: "router-stromkosten",
    key: "router",
    name: "Router",
    icon: "Router",
    mode: "watt",
    h1: "Router Stromkosten berechnen",
    metaTitle: "Router Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein WLAN-Router pro Jahr an Strom? Kleiner Dauerverbraucher, läuft rund um die Uhr. Kostenloser Rechner mit Spanne.",
    intro:
      "Ein Router ist ein kleiner, aber dauerhafter Verbraucher: Er läuft meist rund um die Uhr. Einzeln fällt er kaum auf, übers Jahr summiert er sich trotzdem.",
    ranges: { low: 6, typical: 12, high: 20 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 24, daysPerWeek: 7 },
    price: 0.35,
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 12, min: 2, max: 40, step: 1 },
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 24, min: 1, max: 24, step: 1 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 24, min: 1, max: 24, step: 1 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Wir rechnen mit der typischen Spanne kleiner Dauerverbraucher. Ein einfacher Router liegt am unteren Rand, ein Kombigerät mit Telefonie und vielen Anschlüssen höher.",
    },
    explanation:
      "Der Router wird wie jedes Watt-Gerät gerechnet: Leistung mal Stunden ergibt Kilowattstunden, mal Strompreis die Kosten. Weil er fast immer läuft, rechnen wir standardmäßig mit 24 Stunden am Tag.",
    drivers: [
      "Gerätetyp: ein Kombigerät mit Telefonie und Anschlüssen zieht mehr als ein einfacher Router.",
      "WLAN-Bänder und angeschlossene Geräte.",
      "Laufzeit: rund um die Uhr gegenüber Nachtabschaltung.",
      "Alter des Geräts.",
    ],
    example: {
      label: "Beispiel: 12 Watt, 24 Stunden täglich, 0,35 €/kWh",
      lines: [
        "Verbrauch pro Tag: rund 0,29 kWh",
        "Kosten pro Tag: rund 0,10 €",
        "Kosten pro Monat: rund 3,04 €",
        "Kosten pro Jahr: rund 36,80 €",
      ],
    },
    faq: [
      { q: "Wie viel Strom verbraucht ein Router?", a: "Typisch sind 6 bis 20 Watt, je nach Modell und Ausstattung. Ein Kombigerät mit Telefonie liegt eher am oberen Rand." },
      { q: "Was kostet ein Router im Jahr?", a: "Ein Router mit 12 Watt, der rund um die Uhr läuft, kostet bei 0,35 € pro Kilowattstunde etwa 37 € im Jahr." },
      { q: "Lohnt es sich, den Router nachts abzuschalten?", a: "Eine Nachtabschaltung spart einen Teil der Kosten, ein einfacher Router macht aber selten mehr als ein paar Euro im Jahr aus. Wer Telefonie oder Smart-Home-Geräte nutzt, sollte vorher prüfen, was nachts erreichbar bleiben muss." },
      { q: "Warum läuft der Router meist 24 Stunden?", a: "Damit Internet, Telefonie und verbundene Geräte jederzeit verfügbar sind. Deshalb rechnen wir standardmäßig mit Dauerbetrieb." },
    ],
  },

  gefrierschrank: {
    slug: "gefrierschrank-stromkosten",
    key: "gefrierschrank",
    name: "Gefrierschrank",
    icon: "Snowflake",
    mode: "kwh_per_year",
    h1: "Gefrierschrank Stromkosten berechnen",
    metaTitle: "Gefrierschrank Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Gefrierschrank oder eine Gefriertruhe pro Jahr an Strom? Mit Jahresverbrauch rechnen oder schätzen lassen. Kostenloser Rechner mit Spanne.",
    intro:
      "Ein Gefriergerät läuft rund um die Uhr, deshalb rechnet man am besten über den Jahresverbrauch in Kilowattstunden. Alter, Größe und Effizienzklasse machen den größten Unterschied.",
    ranges: { low: 120, typical: 220, high: 380 },
    rangeUnit: "kWh/Jahr",
    defaultUsage: {},
    exact: {
      fields: [
        { id: "kwhPerYear", label: "Verbrauch pro Jahr", unit: "kWh", default: 220, min: 50, max: 800, step: 10 },
      ],
    },
    estimate: {
      profileFields: [],
      assumptionText:
        "Niedrig steht für ein neues, effizientes Gerät, hoch für ein älteres oder großes. Den genauen Jahreswert findest du oft auf dem Energielabel.",
    },
    explanation:
      "Beim Gefrierschrank zählt der Jahresverbrauch. Diesen multiplizierst du mit dem Strompreis und erhältst die Jahreskosten. Geteilt durch zwölf ergeben sich die Kosten pro Monat.",
    drivers: [
      "Alter: ältere Geräte verbrauchen oft das Doppelte eines neuen.",
      "Größe und Bauform: eine große Truhe braucht mehr als ein kleines Fach.",
      "Effizienzklasse: der Wert auf dem Energielabel ist die beste Grundlage.",
      "Standort: in einem warmen Raum läuft das Gerät häufiger.",
    ],
    example: {
      label: "Beispiel: 220 kWh pro Jahr, 0,39 €/kWh",
      lines: [
        "Kosten pro Jahr: rund 85,80 €",
        "Kosten pro Monat: rund 7,15 €",
      ],
    },
    faq: [
      { q: "Wie viel Strom verbraucht ein Gefrierschrank?", a: "Je nach Alter und Größe meist 120 bis 380 Kilowattstunden im Jahr. Neue Geräte liegen am unteren Rand, alte deutlich darüber." },
      { q: "Wo finde ich den Jahresverbrauch?", a: "Auf dem Energielabel oder im Datenblatt steht der Verbrauch in Kilowattstunden pro Jahr. Mit diesem Wert rechnest du am genauesten." },
      { q: "Lohnt sich ein neues Gerät?", a: "Wenn ein altes Gerät 350 Kilowattstunden zieht und ein neues 150, spart das pro Jahr rund 78 € bei 0,39 € je Kilowattstunde. Über mehrere Jahre kann sich ein Austausch rechnen." },
    ],
  },

  waschmaschine: {
    slug: "waschmaschine-stromkosten",
    key: "waschmaschine",
    name: "Waschmaschine",
    icon: "WashingMachine",
    mode: "kwh_per_cycle",
    h1: "Waschmaschine Stromkosten berechnen",
    metaTitle: "Waschmaschine Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Waschgang an Strom und was die Waschmaschine im Monat? Mit Verbrauch pro Waschgang rechnen oder schätzen lassen.",
    intro:
      "Bei der Waschmaschine zählt vor allem der Verbrauch pro Waschgang, und der hängt stark von der Temperatur ab. Eine Wäsche bei 30 Grad braucht einen Bruchteil einer Kochwäsche.",
    ranges: { low: 0.5, typical: 0.9, high: 1.6 },
    rangeUnit: "kWh/Waschgang",
    defaultUsage: { cyclesPerWeek: 4 },
    exact: {
      fields: [
        { id: "kwhPerCycle", label: "Verbrauch pro Waschgang", unit: "kWh", default: 0.9, min: 0.2, max: 3, step: 0.1 },
        { id: "cyclesPerWeek", label: "Waschgänge pro Woche", unit: "", default: 4, min: 1, max: 30, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "cyclesPerWeek", label: "Waschgänge pro Woche", unit: "", default: 4, min: 1, max: 30, step: 1 },
      ],
      assumptionText:
        "Niedrig entspricht einer Wäsche bei 30 Grad im Eco-Programm, hoch einer Kochwäsche bei 60 bis 90 Grad.",
    },
    explanation:
      "Die Kosten pro Waschgang ergeben sich aus dem Verbrauch in Kilowattstunden mal Strompreis. Mit den Waschgängen pro Woche hochgerechnet erhältst du Monats- und Jahreskosten.",
    drivers: [
      "Temperatur: 30 oder 40 Grad sind deutlich günstiger als 60 oder 90 Grad.",
      "Eco-Programm: spart Strom, dauert aber länger.",
      "Beladung: eine volle Maschine wäscht pro Wäschestück günstiger.",
      "Alter und Effizienzklasse des Geräts.",
    ],
    example: {
      label: "Beispiel: 0,9 kWh pro Waschgang, 4 Waschgänge pro Woche, 0,39 €/kWh",
      lines: [
        "Kosten pro Waschgang: rund 0,35 €",
        "Kosten pro Monat: rund 6,10 €",
        "Kosten pro Jahr: rund 73 €",
      ],
    },
    faq: [
      { q: "Was kostet ein Waschgang?", a: "Bei 0,9 Kilowattstunden und 0,39 € je Kilowattstunde kostet ein Waschgang rund 0,35 €. Bei einer Kochwäsche kann es das Doppelte sein." },
      { q: "Spart das Eco-Programm wirklich Strom?", a: "Ja. Das Eco-Programm wäscht länger bei niedrigerer Temperatur und braucht dadurch weniger Energie, obwohl es länger läuft." },
      { q: "Zählt nur der Strom?", a: "Hier rechnen wir die Stromkosten. Wasser und Waschmittel kommen je Waschgang noch dazu, sind aber meist ein kleinerer Posten." },
    ],
  },

  spuelmaschine: {
    slug: "spuelmaschine-stromkosten",
    key: "spuelmaschine",
    name: "Spülmaschine",
    icon: "UtensilsCrossed",
    mode: "kwh_per_cycle",
    h1: "Spülmaschine Stromkosten berechnen",
    metaTitle: "Spülmaschine Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Spülgang an Strom und was die Spülmaschine im Monat? Mit Verbrauch pro Spülgang rechnen oder schätzen lassen.",
    intro:
      "Den größten Anteil am Verbrauch einer Spülmaschine macht das Aufheizen des Wassers aus. Das Eco-Programm spült kühler und länger und ist deshalb am günstigsten.",
    ranges: { low: 0.7, typical: 1.0, high: 1.5 },
    rangeUnit: "kWh/Spülgang",
    defaultUsage: { cyclesPerWeek: 5 },
    exact: {
      fields: [
        { id: "kwhPerCycle", label: "Verbrauch pro Spülgang", unit: "kWh", default: 1.0, min: 0.3, max: 3, step: 0.1 },
        { id: "cyclesPerWeek", label: "Spülgänge pro Woche", unit: "", default: 5, min: 1, max: 30, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "cyclesPerWeek", label: "Spülgänge pro Woche", unit: "", default: 5, min: 1, max: 30, step: 1 },
      ],
      assumptionText:
        "Niedrig entspricht dem Eco-Programm, hoch einem kurzen oder heißen Intensivprogramm.",
    },
    explanation:
      "Wie bei der Waschmaschine: Verbrauch pro Spülgang mal Strompreis ergibt die Kosten je Spülgang, hochgerechnet auf die Spülgänge pro Woche ergeben sich Monat und Jahr.",
    drivers: [
      "Programm: Eco ist günstiger als Intensiv oder Kurz.",
      "Beladung: voll beladen spült am wirtschaftlichsten.",
      "Vorspülen von Hand kostet zusätzlich Warmwasser.",
      "Alter und Effizienzklasse des Geräts.",
    ],
    example: {
      label: "Beispiel: 1,0 kWh pro Spülgang, 5 Spülgänge pro Woche, 0,39 €/kWh",
      lines: [
        "Kosten pro Spülgang: rund 0,39 €",
        "Kosten pro Monat: rund 8,50 €",
        "Kosten pro Jahr: rund 101 €",
      ],
    },
    faq: [
      { q: "Welches Programm ist am günstigsten?", a: "Das Eco-Programm. Es spült mit weniger heißem Wasser über eine längere Zeit und verbraucht dadurch am wenigsten Strom." },
      { q: "Lohnt sich Vorspülen von Hand?", a: "Meist nicht. Grobe Reste entfernen reicht. Wer mit warmem Wasser vorspült, verbraucht oft mehr, als die Maschine einsparen würde." },
      { q: "Was kostet eine Spülmaschine im Jahr?", a: "Bei einem Spülgang pro Tag und 1,0 Kilowattstunde liegen die Stromkosten bei rund 140 € im Jahr. Mit dem Eco-Programm weniger." },
    ],
  },

  klimaanlage: {
    slug: "klimaanlage-stromkosten",
    key: "klimaanlage",
    name: "Klimaanlage",
    icon: "AirVent",
    mode: "watt",
    h1: "Klimaanlage Stromkosten berechnen",
    metaTitle: "Klimaanlage Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet eine Klimaanlage pro Stunde, Tag und Monat an Strom? Mit Wattzahl rechnen oder schätzen lassen. Kostenloser Rechner mit Spanne.",
    intro:
      "Mobile Monoblock-Klimageräte ziehen viel Strom, fest installierte Splitgeräte sind effizienter. Entscheidend sind Leistung, Raumgröße und wie lange das Gerät täglich läuft.",
    ranges: { low: 800, typical: 1500, high: 3000 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 5, daysPerWeek: 6 },
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 1500, min: 300, max: 4000, step: 100 },
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 5, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 6, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 5, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 6, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Niedrig steht für ein effizientes Splitgerät in einem kleinen Raum, hoch für ein starkes mobiles Monoblock-Gerät.",
    },
    explanation:
      "Leistung in Kilowatt mal Betriebsstunden ergibt den Verbrauch in Kilowattstunden. Klimageräte laufen aber nicht durchgehend auf Volllast, sobald die Wunschtemperatur erreicht ist, taktet das Gerät herunter.",
    drivers: [
      "Geräteart: Splitgeräte sind deutlich sparsamer als mobile Monoblocks.",
      "Raumgröße und Dämmung: mehr Raum braucht mehr Kühlung.",
      "Außentemperatur: an heißen Tagen läuft das Gerät länger.",
      "Wunschtemperatur: jedes Grad kühler kostet zusätzlich.",
    ],
    example: {
      label: "Beispiel: 1.500 Watt, 5 Stunden täglich, 0,39 €/kWh",
      lines: [
        "Verbrauch pro Tag: 7,5 kWh",
        "Kosten pro Tag: rund 2,93 €",
        "Kosten pro Monat: rund 76 €",
      ],
    },
    faq: [
      { q: "Was kostet eine Klimaanlage pro Stunde?", a: "Ein mobiles Gerät mit 1.500 Watt kostet bei 0,39 € je Kilowattstunde rund 0,59 € pro Stunde Volllast. Sobald die Wunschtemperatur erreicht ist, sinkt der Verbrauch." },
      { q: "Sind Splitgeräte günstiger?", a: "Im Betrieb ja. Sie kühlen effizienter und brauchen für dieselbe Wirkung weniger Strom als mobile Monoblock-Geräte." },
      { q: "Warum nur eine Schätzung?", a: "Der reale Verbrauch hängt stark von Außentemperatur, Raum und Taktung ab. Deshalb zeigen wir eine Spanne statt eines einzelnen Werts." },
    ],
  },

  aquarium: {
    slug: "aquarium-stromkosten",
    key: "aquarium",
    name: "Aquarium",
    icon: "Fish",
    mode: "watt",
    h1: "Aquarium Stromkosten berechnen",
    metaTitle: "Aquarium Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Aquarium pro Monat und Jahr an Strom? Heizung, Filter und Licht zusammen rechnen oder schätzen lassen.",
    intro:
      "Ein Aquarium verbraucht Strom über Heizung, Filter und Beleuchtung. Die Heizung läuft nicht durchgehend, sondern taktet. Wir rechnen mit der durchschnittlichen Dauerleistung über den Tag.",
    ranges: { low: 25, typical: 60, high: 150 },
    rangeUnit: "W (Durchschnitt)",
    defaultUsage: { hoursPerDay: 24, daysPerWeek: 7 },
    exact: {
      fields: [
        { id: "watt", label: "Durchschnittliche Leistung (Heizung, Filter, Licht)", unit: "W", default: 60, min: 5, max: 500, step: 5 },
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 24, min: 1, max: 24, step: 1 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 24, min: 1, max: 24, step: 1 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Niedrig steht für ein kleines Nano-Becken, hoch für ein großes Becken mit starker Heizung und Beleuchtung. Der Wert ist die mittlere Leistung über den Tag.",
    },
    explanation:
      "Wir fassen Heizung, Filter und Licht zu einer durchschnittlichen Dauerleistung zusammen. Diese mal 24 Stunden ergibt den Tagesverbrauch. Die Heizung macht in kühlen Räumen den größten Anteil aus.",
    drivers: [
      "Beckengröße: mehr Wasser braucht mehr Heizleistung.",
      "Raumtemperatur: in einem kühlen Raum heizt das Gerät häufiger.",
      "Beleuchtung: LED ist sparsamer als ältere Leuchtmittel.",
      "Zusatztechnik wie CO2 oder Strömungspumpen.",
    ],
    example: {
      label: "Beispiel: 60 Watt im Mittel, 24 Stunden, 0,39 €/kWh",
      lines: [
        "Verbrauch pro Tag: rund 1,44 kWh",
        "Kosten pro Monat: rund 17 €",
        "Kosten pro Jahr: rund 205 €",
      ],
    },
    faq: [
      { q: "Was kostet ein Aquarium im Monat?", a: "Ein mittleres Becken mit rund 60 Watt mittlerer Leistung kostet etwa 17 € im Monat. Kleine Becken deutlich weniger, große mehr." },
      { q: "Was verbraucht am meisten?", a: "Meist die Heizung, vor allem in kühlen Räumen. Filter und LED-Licht sind im Vergleich sparsam." },
      { q: "Wie senke ich die Kosten?", a: "Das Becken in einem wärmeren Raum aufstellen, eine passend dimensionierte Heizung wählen, auf LED setzen und einen Deckel nutzen, damit weniger Wärme entweicht." },
    ],
  },

  fernseher: {
    slug: "fernseher-stromkosten",
    key: "fernseher",
    name: "Fernseher",
    icon: "Tv",
    mode: "watt",
    h1: "Fernseher Stromkosten berechnen",
    metaTitle: "Fernseher Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Fernseher pro Tag, Monat und Jahr an Strom? Mit Wattzahl rechnen oder nach Größe schätzen lassen.",
    intro:
      "Der Verbrauch eines Fernsehers hängt vor allem von Bildschirmgröße und Technik ab. Große Geräte und hohe Helligkeit ziehen mehr, moderne LED-Geräte sind sparsam.",
    ranges: { low: 50, typical: 90, high: 180 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 4, daysPerWeek: 7 },
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 90, min: 20, max: 400, step: 10 },
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 4, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 4, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Niedrig steht für einen kleineren LED-Fernseher, hoch für ein großes Gerät mit über 65 Zoll oder hoher Helligkeit.",
    },
    explanation:
      "Leistung in Kilowatt mal tägliche Nutzungsstunden ergibt den Verbrauch. Den Standby-Verbrauch lassen wir bewusst weg, er ist bei modernen Geräten sehr klein.",
    drivers: [
      "Bildschirmgröße: je größer, desto mehr Strom.",
      "Technik: ältere Geräte und sehr helle Einstellungen brauchen mehr.",
      "Helligkeit und Bildmodus.",
      "Nutzungsdauer pro Tag.",
    ],
    example: {
      label: "Beispiel: 90 Watt, 4 Stunden täglich, 0,39 €/kWh",
      lines: [
        "Verbrauch pro Tag: 0,36 kWh",
        "Kosten pro Monat: rund 4,30 €",
        "Kosten pro Jahr: rund 51 €",
      ],
    },
    faq: [
      { q: "Was kostet ein Fernseher im Jahr?", a: "Ein Gerät mit 90 Watt, das vier Stunden am Tag läuft, kostet bei 0,39 € je Kilowattstunde rund 51 € im Jahr." },
      { q: "Wie viel zieht der Standby?", a: "Bei modernen Geräten meist unter einem Watt. Über das Jahr sind das nur wenige Euro, deshalb fällt es kaum ins Gewicht." },
      { q: "Verbraucht ein großer Fernseher viel mehr?", a: "Ja, die Größe ist der wichtigste Faktor. Ein 75-Zoll-Gerät kann das Drei- bis Vierfache eines kleinen Fernsehers ziehen." },
    ],
  },

  backofen: {
    slug: "backofen-stromkosten",
    key: "backofen",
    name: "Backofen",
    icon: "ChefHat",
    mode: "watt",
    h1: "Backofen Stromkosten berechnen",
    metaTitle: "Backofen Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet eine Stunde Backofen an Strom? Mit Wattzahl und Backzeit rechnen oder schätzen lassen. Kostenloser Rechner mit Spanne.",
    intro:
      "Ein Backofen hat eine hohe Anschlussleistung, heizt aber nicht durchgehend. Sobald die Temperatur erreicht ist, schaltet die Heizung immer wieder ab. Im Mittel liegt der Verbrauch daher unter der Nennleistung.",
    ranges: { low: 1500, typical: 2500, high: 3500 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 0.5, daysPerWeek: 4 },
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 2500, min: 800, max: 4000, step: 100 },
        { id: "hoursPerDay", label: "Backzeit pro Tag", unit: "h", default: 0.5, min: 0.1, max: 8, step: 0.1 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 4, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Backzeit pro Tag", unit: "h", default: 0.5, min: 0.1, max: 8, step: 0.1 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 4, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Niedrig steht für Umluft bei moderater Temperatur, hoch für Ober- und Unterhitze bei hoher Temperatur. In der Praxis taktet die Heizung, der reale Verbrauch liegt eher am unteren Rand.",
    },
    explanation:
      "Wir rechnen mit der Nennleistung mal Backzeit. Weil der Ofen über das Thermostat taktet, ist der tatsächliche Verbrauch meist etwas niedriger als die reine Rechnung mit Volllast.",
    drivers: [
      "Temperatur: höhere Temperatur bedeutet mehr Heizen.",
      "Betriebsart: Umluft ist meist sparsamer als Ober- und Unterhitze.",
      "Vorheizen: oft nicht nötig und kostet zusätzlich.",
      "Backzeit pro Nutzung.",
    ],
    example: {
      label: "Beispiel: 2.500 Watt, 0,5 Stunden, 0,39 €/kWh",
      lines: [
        "Verbrauch pro Nutzung: rund 1,25 kWh",
        "Kosten pro Nutzung: rund 0,49 €",
        "Kosten pro Monat (4-mal pro Woche): rund 8,50 €",
      ],
    },
    faq: [
      { q: "Was kostet eine Stunde Backofen?", a: "Bei 2.500 Watt und 0,39 € je Kilowattstunde kostet eine Stunde Volllast rund 0,98 €. Weil der Ofen taktet, liegt der reale Wert meist darunter." },
      { q: "Spart Umluft Strom?", a: "Oft ja. Umluft verteilt die Wärme gleichmäßiger, du kannst niedriger temperieren und auf mehreren Ebenen backen." },
      { q: "Muss ich vorheizen?", a: "Bei vielen Gerichten nicht. Wer das Vorheizen weglässt, spart einen Teil der Energie." },
    ],
  },

  wasserkocher: {
    slug: "wasserkocher-stromkosten",
    key: "wasserkocher",
    name: "Wasserkocher",
    icon: "CupSoda",
    mode: "watt",
    h1: "Wasserkocher Stromkosten berechnen",
    metaTitle: "Wasserkocher Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Wasserkocher pro Tag, Monat und Jahr an Strom? Mit Wattzahl und Nutzungsdauer rechnen oder schätzen lassen.",
    intro:
      "Ein Wasserkocher hat eine hohe Leistung, läuft aber nur kurz. Über den Tag summieren sich die kurzen Kochvorgänge. Entscheidend sind Wassermenge und wie oft du kochst.",
    ranges: { low: 1800, typical: 2000, high: 2400 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 0.15, daysPerWeek: 7 },
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 2000, min: 600, max: 3000, step: 100 },
        { id: "hoursPerDay", label: "Kochzeit pro Tag (gesamt)", unit: "h", default: 0.15, min: 0.02, max: 3, step: 0.01 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Kochzeit pro Tag (gesamt)", unit: "h", default: 0.15, min: 0.02, max: 3, step: 0.01 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Die meisten Wasserkocher liegen zwischen 1.800 und 2.400 Watt. Eine Tasse braucht etwa eine Minute, ein Liter rund zwei bis drei Minuten.",
    },
    explanation:
      "Leistung mal Kochzeit ergibt den Verbrauch. Weil ein Kochvorgang nur kurz ist, rechnest du am besten die gesamte Kochzeit pro Tag zusammen, also alle Tassen und Liter über den Tag verteilt.",
    drivers: [
      "Wassermenge: nur so viel kochen wie nötig spart spürbar.",
      "Häufigkeit pro Tag.",
      "Resttemperatur: bereits warmes Wasser braucht weniger.",
      "Verkalkung: ein verkalkter Boden heizt langsamer.",
    ],
    example: {
      label: "Beispiel: 2.000 Watt, rund 9 Minuten pro Tag, 0,39 €/kWh",
      lines: [
        "Verbrauch pro Tag: rund 0,3 kWh",
        "Kosten pro Tag: rund 0,12 €",
        "Kosten pro Jahr: rund 43 €",
      ],
    },
    faq: [
      { q: "Was kostet einmal Wasserkochen?", a: "Ein Liter mit einem 2.000-Watt-Kocher braucht rund 0,1 Kilowattstunden, das sind etwa 0,04 € bei 0,39 € je Kilowattstunde." },
      { q: "Spart es Strom, nur eine Tasse zu kochen?", a: "Ja. Wer nur so viel Wasser kocht, wie er braucht, spart am einfachsten, weil weniger Wasser weniger Energie braucht." },
      { q: "Kocht ein Wasserkocher günstiger als der Herd?", a: "Meist ja. Ein Wasserkocher gibt die Wärme direkt ans Wasser ab und ist dadurch effizienter als eine Herdplatte." },
    ],
  },

  kaffeemaschine: {
    slug: "kaffeemaschine-stromkosten",
    key: "kaffeemaschine",
    name: "Kaffeemaschine",
    icon: "Coffee",
    mode: "watt",
    h1: "Kaffeemaschine Stromkosten berechnen",
    metaTitle: "Kaffeemaschine Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet eine Kaffeemaschine oder ein Vollautomat pro Tag und Jahr an Strom? Mit Wattzahl rechnen oder schätzen lassen.",
    intro:
      "Beim Strom zählt vor allem das Aufheizen. Filtermaschinen mit Warmhalteplatte und Vollautomaten, die sich bei jeder Tasse aufheizen, verbrauchen mehr als ein kurzer Brühvorgang.",
    ranges: { low: 800, typical: 1200, high: 1500 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 0.3, daysPerWeek: 7 },
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 1200, min: 400, max: 2500, step: 50 },
        { id: "hoursPerDay", label: "Betriebszeit pro Tag (gesamt)", unit: "h", default: 0.3, min: 0.02, max: 4, step: 0.01 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Betriebszeit pro Tag (gesamt)", unit: "h", default: 0.3, min: 0.02, max: 4, step: 0.01 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Niedrig steht für eine einfache Filtermaschine ohne langes Warmhalten, hoch für einen Vollautomaten mit häufigem Aufheizen.",
    },
    explanation:
      "Leistung mal Betriebszeit ergibt den Verbrauch. Rechne die gesamte aktive Zeit pro Tag zusammen, also Aufheizen, Brühen und gegebenenfalls Warmhalten.",
    drivers: [
      "Geräteart: Vollautomat, Filtermaschine oder Kapselgerät.",
      "Warmhalteplatte: hält den Kaffee warm und kostet dauerhaft Strom.",
      "Anzahl der Tassen pro Tag.",
      "Automatische Abschaltung: spart Standby und Warmhalten.",
    ],
    example: {
      label: "Beispiel: 1.200 Watt, rund 18 Minuten pro Tag, 0,39 €/kWh",
      lines: [
        "Verbrauch pro Tag: rund 0,36 kWh",
        "Kosten pro Tag: rund 0,14 €",
        "Kosten pro Jahr: rund 51 €",
      ],
    },
    faq: [
      { q: "Was kostet eine Tasse Kaffee an Strom?", a: "Je nach Gerät meist zwischen 0,01 und 0,03 €. Den größten Anteil hat das Aufheizen, nicht das Brühen selbst." },
      { q: "Frisst die Warmhalteplatte viel Strom?", a: "Über den Tag schon. Wer den Kaffee in eine Thermoskanne füllt und die Platte abschaltet, spart spürbar." },
      { q: "Verbraucht ein Vollautomat mehr?", a: "Tendenziell ja, weil er sich für jede Tasse aufheizt und oft im Standby bleibt. Eine kurze automatische Abschaltung hilft." },
    ],
  },

  luftentfeuchter: {
    slug: "luftentfeuchter-stromkosten",
    key: "luftentfeuchter",
    name: "Luftentfeuchter",
    icon: "Droplets",
    mode: "watt",
    h1: "Luftentfeuchter Stromkosten berechnen",
    metaTitle: "Luftentfeuchter Stromkosten berechnen | Gerätekosten",
    metaDescription:
      "Was kostet ein Luftentfeuchter pro Tag, Monat und Jahr an Strom? Mit Wattzahl und Laufzeit rechnen oder schätzen lassen.",
    intro:
      "Ein Luftentfeuchter läuft oft viele Stunden am Tag, deshalb summieren sich auch moderate Leistungen. Kompressorgeräte sind sparsamer als kleine Geräte mit Granulat- oder Peltier-Technik je entzogenem Liter.",
    ranges: { low: 200, typical: 400, high: 700 },
    rangeUnit: "W",
    defaultUsage: { hoursPerDay: 8, daysPerWeek: 7 },
    exact: {
      fields: [
        { id: "watt", label: "Leistung", unit: "W", default: 400, min: 30, max: 1200, step: 10 },
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 8, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
    },
    estimate: {
      profileFields: [
        { id: "hoursPerDay", label: "Stunden pro Tag", unit: "h", default: 8, min: 0.5, max: 24, step: 0.5 },
        { id: "daysPerWeek", label: "Tage pro Woche", unit: "Tage", default: 7, min: 1, max: 7, step: 1 },
      ],
      assumptionText:
        "Niedrig steht für ein kleines, effizientes Kompressorgerät, hoch für ein starkes Gerät oder ein weniger effizientes Prinzip.",
    },
    explanation:
      "Leistung in Kilowatt mal Laufzeit ergibt den Verbrauch. Viele Geräte regeln über einen Feuchtigkeitssensor und laufen nicht durchgehend, was den realen Verbrauch senkt.",
    drivers: [
      "Technik: Kompressorgeräte sind je Liter meist am sparsamsten.",
      "Laufzeit pro Tag.",
      "Zielfeuchte: je trockener gewünscht, desto länger läuft das Gerät.",
      "Raumgröße und Feuchtequelle.",
    ],
    example: {
      label: "Beispiel: 400 Watt, 8 Stunden täglich, 0,39 €/kWh",
      lines: [
        "Verbrauch pro Tag: 3,2 kWh",
        "Kosten pro Tag: rund 1,25 €",
        "Kosten pro Monat: rund 38 €",
      ],
    },
    faq: [
      { q: "Was kostet ein Luftentfeuchter im Monat?", a: "Ein Gerät mit 400 Watt, das acht Stunden am Tag läuft, kostet bei 0,39 € je Kilowattstunde rund 38 € im Monat." },
      { q: "Welche Technik ist am sparsamsten?", a: "Für normale Wohnräume sind Kompressorgeräte je entzogenem Liter Wasser meist am effizientesten. Sehr kleine Peltier-Geräte brauchen pro Liter mehr." },
      { q: "Wie senke ich die Kosten?", a: "Ein Hygrostat, der das Gerät bei Erreichen der Zielfeuchte abschaltet, sowie das Lüften und Beseitigen der Feuchtequelle helfen am meisten." },
    ],
  },
};

// Reihenfolge für Startseite und interne Links (PRD §20)
export const deviceOrder = [
  "heizluefter",
  "klimaanlage",
  "trockner",
  "waschmaschine",
  "spuelmaschine",
  "gaming-pc",
  "fernseher",
  "kuehlschrank",
  "gefrierschrank",
  "backofen",
  "wasserkocher",
  "kaffeemaschine",
  "aquarium",
  "luftentfeuchter",
  "router",
];

export const deviceList = deviceOrder.map((k) => devices[k]);

export function getDeviceBySlug(slug) {
  return Object.values(devices).find((d) => d.slug === slug) || null;
}

// Alle PRD-§8.1-Geräte sind als eigene Seite gebaut. Liste bleibt für
// optionale spätere Ergänzungen (z. B. Balkonkraftwerk).
export const comingSoon = [];
